import ClassFormEnrollmentSection from '@/components/ClassFormEnrollmentSection';
import ClassFormGeneralSettingsSection from '@/components/ClassFormGeneralSettingsSection';
import ClassFormHeader from '@/components/ClassFormHeader';
import ClassPeriod from '@/components/ClassPeriod';
import GoBackMenu from '@/components/GoBackNavbar';
import ImportHistory from '@/components/ImportHistory';
import Loader from '@/components/Loader';
import Constants from '@/Constants';
import { useGet } from '@/hooks/useGet';
import List from '@/models/List';
import { ProgramClass } from '@/models/ProgramClass';
import { getNewDate } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { deleteProgramClassRequest, updateProgramClassRequest } from '@/store/programClass/actions';
import { LocaleData } from '@/translations/LocaleData';
import { format } from 'date-fns';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import _ from 'underscore';
import FindPersonModal from '../FindPersonModal';
import { formInitialState, ProgramClassForm, programClassSchema } from './formConfig';
import './style.scss';

interface Props {
    entity: List;
}

export enum DateFieldNames {
    startDate = 'startDate',
    finishDate = 'finishDate',
}

const formatDateToString = (date: Date): string => {
    return `${format(date, 'yyyy-MM-dd')}T00:00:00.000Z`;
};

const RegularClassForm: React.FC<Props> = (props: Props) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const { entity } = props;

    const { classId } = useParams<{ classId: string }>();

    const { data: programClass, loading, revalidateResult, mutate } = useGet<ProgramClass>(
        `${Constants.LMS_PROGRAM_CLASS_URI}/${classId}?withEnrollmentsCount=true`,
    );

    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);
    const applicationId: number = useSelector((state: ApplicationState) => state.identity.data.user.applicationId);
    const orgId: number = useSelector((state: ApplicationState) => state.identity.data.user.orgId);

    const [formState, setFormState] = useState(formInitialState);
    const [formStateBkp, setFormStateBkp] = useState(formInitialState);

    const { name, startDate, finishDate, enrollmentsCount, enableCourseCertificate, mandatory }: ProgramClassForm = formState;

    const [showFindPersonModal, setShowFindPersonModal] = useState<boolean>(false);
    const [showOnlyEnrolledInModal, setShowOnlyEnrolledInModal] = useState<boolean>(false);
    const [deleteAllowed, setDeleteAllowed] = useState<boolean>(false);
    const [formIsValid, setFormIsValid] = useState<boolean>(false);
    const [totalEnrollmentsCount, setTotalEnrollmentsCount] = useState<number>(enrollmentsCount);

    const batchUpdateForm = (editingProgramClass: ProgramClass) => {
        const auxStartDate: Date | undefined = editingProgramClass.startDate ? editingProgramClass.startDate : editingProgramClass.scheduledStartDate;

        handleOnChange('name', editingProgramClass.name, true);
        handleOnChange('startDate', auxStartDate ? getNewDate(auxStartDate) : undefined, true);
        handleOnChange('finishDate', editingProgramClass.finishDate ? getNewDate(editingProgramClass.finishDate) : undefined, true);
        handleOnChange('enrollmentsCount', editingProgramClass.enrollmentsCount, true);
        handleOnChange('enableCourseCertificate', editingProgramClass.hasCourseCertification, true);
        handleOnChange('mandatory', editingProgramClass.mandatory, true);
    };

    const handleOnChange = (fieldName: string, value: string | Date | number | boolean | undefined, isBkp: boolean = false) => {
        setFormState((prevState: any) => ({ ...prevState, [fieldName]: value }));
        if (isBkp) {
            setFormStateBkp((prevState: any) => ({ ...prevState, [fieldName]: value }));
        }
    };

    const onSaveClickHandler = () => {
        if (programClass) {
            const scheduledStartDateAsString: string | null = !programClass.startDate
                ? startDate
                    ? formatDateToString(startDate as Date)
                    : null
                : null;

            const programClassToUpdate: ProgramClass = {
                ...programClass,
                name,
                startDate,
                scheduledStartDateAsString,
                scheduledStartDate: programClass.scheduledStartDate,
                finishDateAsString: finishDate ? moment(finishDate).format('YYYY-MM-DD') : null,
                finishDate,
                mandatory,
                hasCourseCertification: enableCourseCertificate,
            } as ProgramClass;

            dispatch(updateProgramClassRequest(programClassToUpdate));

            mutate(programClassToUpdate, false);

            handleWaitAndReturn();
        }
    };

    const onDeleteClickHandler = () => {
        dispatch(deleteProgramClassRequest(Number(classId)));
        handleWaitAndReturn();
    };

    const handleWaitAndReturn = () => {
        setTimeout(() => history.goBack(), 1000);
    };

    useEffect(() => {
        if (!!programClass) {
            batchUpdateForm(programClass);
        }
    }, [programClass?.id]);

    useEffect(() => {
        if (!!programClass) {
            setTotalEnrollmentsCount(programClass.enrollmentsCount || 0);
        }
    }, [programClass?.enrollmentsCount]);

    useEffect(() => {
        if (!!programClass) {
            setDeleteAllowed(!programClass.startDate && totalEnrollmentsCount === 0);
        }
    }, [programClass?.startDate, totalEnrollmentsCount]);

    useEffect(() => {
        if (entity.id) {
            programClassSchema
                .validate(formState)
                .then(() => setFormIsValid(true))
                .catch(() => setFormIsValid(false));
        }
    }, [entity, formState]);

    return (
        <div className="regular-class-form">
            <GoBackMenu confirmable={!_.isEqual(formStateBkp, formState)} />

            {programClass && (
                <FindPersonModal
                    enrollmentsCount={totalEnrollmentsCount}
                    onlyEnrolled={showOnlyEnrolledInModal}
                    programClassId={programClass.id}
                    setShow={setShowFindPersonModal}
                    setTotalEnrollments={setTotalEnrollmentsCount}
                    show={showFindPersonModal}
                />
            )}
            <div className="class-form">
                <div className="class-header">
                    <span>{strings.MANAGE_CLASSES}</span>
                    <div className="container-description">
                        {programClass && (
                            <span className="item-type">{programClass?.program.isCourseProgram ? strings.COURSE : strings.PROGRAM}</span>
                        )}
                        <h4>{entity.name}</h4>
                    </div>
                </div>

                <div className="class-box-container">
                    {loading ? (
                        <Loader />
                    ) : (
                        <>
                            <ClassFormHeader
                                deleteAllowed={deleteAllowed}
                                formIsInvalid={!formIsValid || _.isEqual(formStateBkp, formState)}
                                handleOnChange={handleOnChange}
                                name={name}
                                onCancelClickHandler={() => history.goBack()}
                                onDeleteClickHandler={onDeleteClickHandler}
                                onSaveClickHandler={onSaveClickHandler}
                            />
                            <div className="class-form-container">
                                <ClassFormGeneralSettingsSection
                                    enableCourseCertificate={enableCourseCertificate}
                                    handleOnChange={handleOnChange}
                                    mandatory={mandatory}
                                />

                                <section className="input-section">
                                    <ClassPeriod
                                        finishDateForm={finishDate}
                                        onChange={({ startDate, finishDate }) => {
                                            handleOnChange('startDate', startDate);
                                            handleOnChange('finishDate', finishDate);
                                        }}
                                        startDateForm={startDate}
                                    />
                                </section>

                                <ClassFormEnrollmentSection
                                    applicationId={applicationId}
                                    organizationId={orgId}
                                    programClassId={Number(classId)}
                                    setShowFindPersonModal={setShowFindPersonModal}
                                    setShowOnlyEnrolledInModal={setShowOnlyEnrolledInModal}
                                    totalEnrollmentsCount={totalEnrollmentsCount}
                                />

                                <div className="input-section">
                                    {programClass && <ImportHistory onRefresh={revalidateResult} programClass={programClass} />}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RegularClassForm;
